'use client';

import React, { useEffect, useRef, useState } from 'react';

const BRAND = '#3757FF';

const categories = [
    { label: 'Mathematics', count: '240+ programs', color: 'rgba(55,87,255,0.08)', accent: BRAND },
    { label: 'Sciences', count: '180+ bootcamps', color: 'rgba(16,185,129,0.08)', accent: '#10B981' },
    { label: 'Video Editing', count: '120+ bootcamps', color: 'rgba(245,158,11,0.08)', accent: '#F59E0B' },
    { label: 'Technology', count: '95+ programs', color: 'rgba(239,68,68,0.08)', accent: '#EF4444' },
    { label: 'Arts & Music', count: '75+ bootcamps', color: 'rgba(168,85,247,0.08)', accent: '#A855F7' },
    { label: 'Food & Nutrition', count: '60+ programs', color: 'rgba(20,184,166,0.08)', accent: '#14B8A6' },
];

const programCards = [
    { subject: 'Mathematics', title: 'Advanced Calculus', institution: 'University Of Knowledge', level: '100 Level', enrolled: '1.2k', rating: '4.9' },
    { subject: 'Physics', title: 'Mechanics & Motion', institution: 'Global Academy', level: 'Grade 11', enrolled: '890', rating: '4.8' },
    { subject: 'English', title: 'Essay Writing Mastery', institution: 'Elite University', level: '100 Level', enrolled: '2.1k', rating: '4.9' },
];

export default function LearnDiscover() {
    const sectionRef = useRef<HTMLElement>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setVisible(true); }, { threshold: 0.1 });
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <section id="discover" ref={sectionRef} className="py-28 px-6 md:px-10 relative overflow-hidden" style={{ background: '#fff' }}>
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 10% 50%, rgba(55,87,255,0.04) 0%, transparent 60%)' }} />

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Header */}
                <div className={`mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    <div
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-700 uppercase tracking-widest mb-6"
                        style={{ background: 'rgba(55,87,255,0.08)', color: BRAND, border: '1px solid rgba(55,87,255,0.15)', letterSpacing: '0.15em' }}
                    >
                        Institution Discovery
                    </div>
                    <div className="grid lg:grid-cols-2 gap-8 items-end">
                        <h2
                            className="font-display font-800"
                            style={{ fontSize: 'clamp(1.8rem, 3.5vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.025em', color: '#0D0D0D' }}
                        >
                            Find exactly what
                            <br />
                            <span style={{ color: BRAND }}>you need to learn.</span>
                        </h2>
                        <p className="text-base leading-relaxed" style={{ color: '#666', maxWidth: '420px' }}>
                            Browse hundreds of programs across different institutions. Filter by grade, format, tutor, or availability — and enroll in seconds.
                        </p>
                    </div>
                </div>

                {/* Category tiles — asymmetric bento */}
                <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-16 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    {categories.map((cat) => (
                        <div
                            key={cat.label}
                            className="rounded-2xl p-5 cursor-pointer transition-all duration-300 group"
                            style={{ background: cat.color, border: `1px solid ${cat.accent}20` }}
                            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)'; (e.currentTarget as HTMLElement).style.boxShadow = `0 12px 32px ${cat.accent}20`; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}
                        >
                            <p className="text-sm font-800 mb-1" style={{ color: '#0D0D0D' }}>{cat.label}</p>
                            <p className="text-xs font-600" style={{ color: cat.accent }}>{cat.count}</p>
                        </div>
                    ))}
                </div>

                {/* Program cards */}
                <div className={`grid md:grid-cols-3 gap-5 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    {programCards.map((program, i) => (
                        <div
                            key={program.title}
                            className="rounded-2xl p-6 cursor-pointer transition-all duration-300"
                            style={{ background: '#FAFAFA', border: '1px solid rgba(55,87,255,0.08)', transitionDelay: `${i * 80}ms` }}
                            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(55,87,255,0.25)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 12px 40px rgba(55,87,255,0.1)'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(55,87,255,0.08)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}
                        >
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-xs font-700 px-3 py-1.5 rounded-full" style={{ background: 'rgba(55,87,255,0.08)', color: BRAND }}>{program.subject}</span>
                                <span className="text-xs font-600" style={{ color: '#888' }}>{program.level}</span>
                            </div>
                            <h4 className="text-base font-800 mb-2" style={{ color: '#0D0D0D', letterSpacing: '-0.01em' }}>{program.title}</h4>
                            <p className="text-sm font-600 mb-5" style={{ color: '#666' }}>{program.institution}</p>
                            <div className="flex items-center justify-between pt-4" style={{ borderTop: '1px solid rgba(55,87,255,0.08)' }}>
                                <div className="flex items-center gap-1.5">
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 1l1.5 3 3.5.5-2.5 2.5.5 3.5L6 9l-3 1.5.5-3.5L1 4.5 4.5 4z" fill={BRAND} /></svg>
                                    <span className="text-xs font-700" style={{ color: '#0D0D0D' }}>{program.rating}</span>
                                </div>
                                <span className="text-xs font-600" style={{ color: '#888' }}>{program.enrolled} enrolled</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
